import {
  getGasAnalysisFutureProdComparisonTable,
  getGasAnalysisFutureProdDaily,
  getGasAnalysisFutureProductsChart,
} from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { GraphIndicators } from "$/lib/components/graphs/GraphIndicator/GraphIndicators";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Tabs, TextInput } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import { displayMessage } from "$/lib/components/molecules/CustomToast/CustomToast.utils";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import { RenderTableNumberCell } from "$/lib/components/widgets/TableGraphWidget/RenderTableNumberCell";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { GasFutureProdComparisonTableType } from "$/lib/types/api/market-monitoring/gas-analysis.types";
import type { CellType, ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import { DEFAULT_GRAPH_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  handleOnChangeDefaultGraphsTabs,
  PEG_GAS_COLORS,
  TTF_GAS_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys, getMinMax } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

// const PEG_COLORS = ["#2e6d38", "#67a158", "#b3c365", "#2e6d3850"];
// const TTF_COLORS = ["#aa154a", "#e56453", "#f09883", "#aa154a80"];

// const COLORS = [...PEG_COLORS, ...TTF_COLORS];

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const GasAnalysisFutureProductsChart = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    peg: boolean;
    ttf: boolean;
  }>({
    peg: true,
    ttf: false,
  });

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const [amplitude, setAmplitude] = useState<number>(20);

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-analysis-future-prod-chart", dateRange, amplitude],
    queryFn: () =>
      getGasAnalysisFutureProductsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        peg: filters.peg,
        ttf: filters.ttf,
        amplitude,
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: [
      "get-gas-analysis-future-prod-comparison-table",
      dateRange,
      tablePeriod,
    ],
    queryFn: () =>
      getGasAnalysisFutureProdComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-gas-analysis-future-prod-daily", selectedDate],
    queryFn: () =>
      getGasAnalysisFutureProdDaily({ date: getDate(selectedDate) }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.peg && !filters.ttf
          ? data.pegProductsData
          : filters.ttf && !filters.peg
            ? data.ttfProductsData
            : filters.peg && filters.ttf
              ? data.pegAndttfData
              : []
        : [],
    [data, filters],
  );
  const toDisplayVolatility = useMemo(
    () =>
      data
        ? filters.peg && !filters.ttf
          ? data.pegProductsVolatilityData
          : filters.ttf && !filters.peg
            ? data.ttfProductsVolatilityData
            : filters.peg && filters.ttf
              ? data.pegAndttfVolatilityData
              : []
        : [],
    [data, filters],
  );

  const colorsToDisplay = useMemo(() => {
    if (filters.peg && filters.ttf) {
      return [...PEG_GAS_COLORS, ...TTF_GAS_COLORS];
    }

    return filters.peg ? PEG_GAS_COLORS : TTF_GAS_COLORS;
  }, [filters]);

  const pegKeys = useMemo(
    () =>
      getArrayKeys(data?.pegProductsData ?? []).filter(
        (key) => !key.includes("MM"),
      ),
    [data],
  );
  const ttfKeys = useMemo(
    () =>
      getArrayKeys(data?.ttfProductsData ?? []).filter(
        (key) => !key.includes("MM"),
      ),
    [data],
  );

  const keys = useMemo(
    () =>
      filters.peg && filters.ttf
        ? [...pegKeys, ...ttfKeys]
        : filters.peg
          ? pegKeys
          : filters.ttf
            ? ttfKeys
            : [],
    [filters, pegKeys, ttfKeys],
  );

  const avgKeys = useMemo(
    () => getArrayKeys(toDisplay).filter((key) => key.includes("MM")),
    [toDisplay],
  );

  const extraKeys = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]: { dataKey: avgKeys[i] },
        }),
        {},
      ),
    [avgKeys, keys],
  );

  const pegTableKeys = ["cal1PegAvg", "cal2PegAvg", "cal3PegAvg"];
  const ttfTableKeys = ["cal1TtfAvg", "cal2TtfAvg", "cal3TtfAvg"];
  const allTableKeys = [...pegTableKeys, ...ttfTableKeys];

  const tableBounds = useMemo(() => {
    if (tableData) {
      const keysToCompare =
        filters.peg && filters.ttf
          ? allTableKeys
          : filters.peg
            ? pegTableKeys
            : ttfTableKeys;

      return getMinMax(tableData[tablePeriod], keysToCompare);
    }

    return { min: { value: 0 }, max: { value: 0 } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tablePeriod, filters]);

  const columns: ColumnType<GasFutureProdComparisonTableType>[] =
    useMemo(() => {
      const firstHeaders: ColumnType<GasFutureProdComparisonTableType>[] = [
        {
          header: () => {},
          accessorKey: "year",
          cell: (cell) => {
            const values = cell.row.original;

            const display =
              tablePeriod === "QUARTER" || tablePeriod === "MONTH"
                ? [values.quarter, values.month]
                : [values.month, values.day];

            return (
              <Flexbox className="gap-0.5 whitespace-nowrap">
                <span className="text-xs font-medium text-black">
                  {cell.getValue()}
                </span>

                <span className="text-[10px] text-tertiary-600">
                  {display
                    .filter((v) => v)
                    .join(tablePeriod === "DAY" ? " " : " - ")}
                </span>
              </Flexbox>
            );
          },
        },
      ];

      return [
        ...firstHeaders,
        ...(filters.peg
          ? [
              {
                header: "PEG Cal +1",
                accessorKey: "cal1PegAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
              {
                header: "PEG Cal +2",
                accessorKey: "cal2PegAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
              {
                header: "PEG Cal +3",
                accessorKey: "cal3PegAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
            ]
          : []),
        ...(filters.ttf
          ? [
              {
                header: "TTF Cal +1",
                accessorKey: "cal1TtfAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
              {
                header: "TTF Cal +2",
                accessorKey: "cal2TtfAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
              {
                header: "TTF Cal +3",
                accessorKey: "cal3TtfAvg",
                cell: (cell: CellType<GasFutureProdComparisonTableType>) => (
                  <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
                ),
              },
            ]
          : []),
      ];
    }, [filters, tableBounds, tablePeriod]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: colorsToDisplay[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color: colorsToDisplay[i],
      })),
    );
  }, [colorsToDisplay, keys]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title="Calendaires et Moyennes Mobiles"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      MiddleComponent={
        <Tabs
          className="mx-auto"
          type="bordered"
          tabs={DEFAULT_GRAPH_TABS}
          activeTab={selectedGraphTabId}
          handleTabChange={setSelectedGraphTabId}
          theme="green"
        />
      }
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId={`id-gas-analysis-future-prod`}
                colors={colors}
                theme="green"
                keys={keys}
                extraKeys={extraKeys}
                heightPercent={65}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId={`id-gas-analysis-future-prod`}
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="green"
                keys={keys}
                heightPercent={40}
                yAxisTicksNumber={7}
                leftMargin={-10}
                setIndices={setIndices}
                indices={indices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <CheckboxSet<"peg" | "ttf">
            id="gas-market-peg-ttf"
            title="Marché"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.peg && !newValue.ttf) return prev;
                return newValue;
              })
            }
            options={[
              { id: "peg", label: "PEG" },
              { id: "ttf", label: "TTF" },
            ]}
            className="items-center"
            row
          />

          <TextInput
            label="Amplitude moyenne mob."
            value={amplitude}
            type="number"
            className="w-[100px]"
            labelClassName="whitespace-nowrap text-base font-semibold text-secondary"
            onChange={(e) => {
              const value = +e.target.value;

              if (value >= 0 && value <= 50) {
                setAmplitude(+e.target.value);

                return;
              }

              displayMessage(
                "L'amplitude doit être comprise entre 0 et 50",
                "warning",
              );
            }}
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.length && (
              <Flexbox fullWidth>
                <GraphIndicators
                  selectedDate={selectedDate}
                  data={daily ?? []}
                  indicatorsTitles={{
                    title1: "PEG",
                    title2: "TTF",
                  }}
                />
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<GasFutureProdComparisonTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
                theme="green"
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasAnalysisFutureProductsChart;
